import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://fusiondocs.com';
const SITE_NAME = 'FusionDocs';

const defaultMeta = {
  title: 'FusionDocs — Governed Document Intelligence',
  description:
    'FusionDocs is a governed document intelligence platform. Capture business processes from any source, enforce document governance by design, and expose your knowledge natively to AI agents — without vendor lock-in.',
  image: `${SITE_URL}/og-image.png`,
  url: SITE_URL,
};

// JSON-LD schemas for AEO (Answer Engine Optimization)
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  description:
    'Governed document intelligence platform — AI-native, no vendor lock-in.',
  parentOrganization: {
    '@type': 'Organization',
    name: 'SaigonAI',
    url: 'https://saigonai.com',
  },
  sameAs: [],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: defaultMeta.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: SITE_NAME,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Linux, Docker',
  description:
    'A governed document intelligence platform that captures business processes from any source, enforces document governance by design, and exposes structured knowledge natively to AI agents and automations — without vendor lock-in.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/ComingSoon',
  },
  featureList: [
    'Process capture from any source (forms, API, file upload, automations)',
    'Enforced document governance — wrong folder is architecturally rejected',
    'AI-native smart documents — live, canonically addressed, structured JSON',
    'Hybrid keyword + semantic search with access control at query level',
    'Native electronic signature with tamper-evident audit trail',
    'Real-time collaborative editing (Y.js CRDT)',
    'Data portability — export your data at any time, no lock-in',
    'No vendor lock-in — MIT/Apache licensed stack',
  ],
  creator: {
    '@type': 'Organization',
    name: 'SaigonAI',
    url: 'https://saigonai.com',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is FusionDocs?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FusionDocs is a governed document intelligence platform. It is a single system where your company\'s structured knowledge must live, is always findable, and never walks out the door. Unlike Google Drive or SharePoint, FusionDocs enforces document governance by architecture — documents must be created in designated projects and folders, and wrong placement is rejected.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is FusionDocs different from Google Drive or Microsoft 365?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Google Drive and Microsoft 365 are optimized for creation, not governance. Anyone can save anything anywhere. FusionDocs enforces the perimeter — documents can only be created inside designated projects following defined DocType schemas. When an employee leaves, their documents stay. When you need something from six months ago, it is searchable. No copy of your data sits behind someone else\'s paywall.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does FusionDocs lock you in?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. FusionDocs is built on an open stack (MIT/Apache 2.0 licensed dependencies) and your data is always exportable. There is no proprietary format or vendor dependency that traps your documents. If you ever leave, your files and structured metadata remain intact and readable.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can AI agents and automations access FusionDocs documents?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Every document in FusionDocs is a smart document: a live, canonically addressed record that AI agents, automations, and integrations can consume directly. Documents are structured, versioned, and context-rich. No export steps, no format conversion, no loss of governance. The document stays inside the perimeter; the intelligence comes to it.',
      },
    },
    {
      '@type': 'Question',
      name: 'What technology stack does FusionDocs use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FusionDocs is built on FastAPI + Python, PostgreSQL + pgvector for hybrid search, AWS S3 for document storage, Y.js + Hocuspocus for CRDT collaborative editing, Gotenberg + PDF.js for document preview, and React for the admin interface. Every dependency passes a hard license filter: MIT, Apache 2.0, BSD, or MPL 2.0 only — no AGPL, no GPL.',
      },
    },
    {
      '@type': 'Question',
      name: 'When will FusionDocs be available?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FusionDocs is currently in early development. Join the early access list at fusiondocs.com to be notified when access opens.',
      },
    },
  ],
};

export default function SEO({ title, description, url, image }) {
  const meta = {
    title: title || defaultMeta.title,
    description: description || defaultMeta.description,
    url: url || defaultMeta.url,
    image: image || defaultMeta.image,
  };

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.url} />

      {/* Open Graph */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={meta.url} />
      <meta property="og:image" content={meta.image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.image} />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
}
